import { CustomListItemId } from "@sp-crm/core";
import { Badge } from "components/ui/badge";
import { Spinner } from "components/ui/spinner";
import * as React from "react";
import { useDrag } from "react-dnd";
import { useCustomList } from "store/selectors/bridge";
import { DroppableWrapper } from "../../util/dnd";
import { Icon } from "../icon";
import { EmptyCard } from "./card";
import { pipelineStatusDate } from "./pipeline-status-date";
import type { PipelineConfig, PipelineItem } from "./types";

interface DashboardPlaceholderColumnProps {
    token: string;
    pipelinePrefix: string;
    humanFriendly: string;
    icon: string;
}

interface PipelineColumnHeaderProps {
    icon: string;
    title: string;
    badge: React.ReactNode;
}

export const PipelineColumnHeader: React.FC<PipelineColumnHeaderProps> = ({
    icon,
    title,
    badge,
}) => (
    <div className="pipeline-header">
        <div className="pipeline-header-icon">
            <Icon name={icon || "49_Info"} />
        </div>
        <div className="pipeline-header-title">{title}</div>
        <div className="pipeline-header-badge">{badge}</div>
    </div>
);

export const DashboardPlaceholderColumn: React.FC<DashboardPlaceholderColumnProps> = ({
    token,
    pipelinePrefix,
    humanFriendly,
    icon,
}) => {
    return (
        <div className={`col pipeline-stage pipeline-stage-${token}`}>
            <div className={"pipeline-innards"}>
                <PipelineColumnHeader
                    icon={icon}
                    title={pipelinePrefix + humanFriendly}
                    badge={<Spinner />}
                />
            </div>
        </div>
    );
};

export const DraggableCard: React.FC<{
    id: string;
    type: string;
    onDrop: (targetId: string) => void;
    children: React.ReactNode;
}> = ({ id, type, onDrop, children }) => {
    const [{ opacity }, drag] = useDrag({
        item: { id, type },
        type,
        end(item, monitor) {
            const dropResult = monitor.getDropResult() as {
                id: string;
                allowedDropEffect: string;
                dropEffect: string;
            } | null;
            if (item && dropResult) {
                const isDropAllowed =
                    dropResult.allowedDropEffect === "any" ||
                    dropResult.allowedDropEffect === dropResult.dropEffect;
                if (isDropAllowed) {
                    onDrop(dropResult.id);
                }
            }
        },
        collect: monitor => ({
            opacity: monitor.isDragging() ? 0.4 : 1,
        }),
    });
    return (
        <div ref={drag} style={{ opacity }}>
            {children}
        </div>
    );
};

interface DashboardColumnProps {
    id: CustomListItemId;
    token: string;
    pipelinePrefix: string;
    humanFriendly: string;
    icon: string;
    config: PipelineConfig;
    dragHandler: (
        entityId: string,
        existingStatus: CustomListItemId,
        newStatus: CustomListItemId,
    ) => void;
    pendingEntities: string[];
    highlightedEntities: string[];
    showAssignedTo: boolean;
    searchCriteria: Record<string, unknown>;
}

export const DraggableDashboardColumn: React.FC<DashboardColumnProps> = props => {
    const {
        id,
        token,
        pipelinePrefix,
        humanFriendly,
        icon,
        config,
        dragHandler,
        pendingEntities,
        highlightedEntities,
        showAssignedTo,
        searchCriteria,
    } = props;

    const { getListItem } = useCustomList(config.customListKey);
    const { data: items } = config.useColumnQuery(id, searchCriteria);

    const getStatusDescription = React.useCallback(
        (item: PipelineItem) =>
            pipelineStatusDate(getListItem(item.statusListItemId), item),
        [getListItem],
    );

    if (!items) {
        return <DashboardPlaceholderColumn {...props} />;
    }

    return (
        <div className={`col pipeline-stage pipeline-stage-${token}`}>
            <DroppableWrapper accept={config.cardType} id={id}>
                <div className={"pipeline-innards"}>
                    <PipelineColumnHeader
                        icon={icon}
                        title={pipelinePrefix + humanFriendly}
                        badge={<Badge type="info" value={items.length} />}
                    />
                    <div className="pipeline-content">
                        {items.length > 0 ? (
                            items.map(item => {
                                if (!item.statusListItemId) {
                                    return null;
                                }
                                return (
                                    <DraggableCard
                                        key={item.id}
                                        id={item.id}
                                        type={config.cardType}
                                        onDrop={(target: string) =>
                                            dragHandler(
                                                item.id,
                                                item.statusListItemId,
                                                target as CustomListItemId,
                                            )
                                        }>
                                        {config.renderCard(item, {
                                            highlightCard: highlightedEntities.includes(
                                                item.id,
                                            ),
                                            pendingCard: pendingEntities.includes(
                                                item.id,
                                            ),
                                            showAssignedTo,
                                            getStatusDescription,
                                        })}
                                    </DraggableCard>
                                );
                            })
                        ) : (
                            <EmptyCard />
                        )}
                    </div>
                </div>
            </DroppableWrapper>
        </div>
    );
};
